import React, { Component } from "react";
import { connect } from "react-redux";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import * as actions from "../../../store/actions/index";
import axios from "../../../axios-url";
// import "./KOTCommon.scss";

// ============================================================================
// PackingDashboard — Packing page
// One card per kitchen-station KOT (actiontype === 2). Row tap only moves an
// item between Pending and Preparing. Reaching Ready is a deliberate,
// manual action via the checkbox — never automatic.
// ============================================================================

const toggleKitchenStage = (s) => (s === "pending" ? "preparing" : "pending");

// ⚠️ statusId -> stage mapping. Sample payload has statusId: null, so this
// is a placeholder — replace the numeric keys with your real status-master
// IDs for Pending / Preparing / Ready once confirmed.
const STATUS_ID_TO_STAGE = {
  1: "pending",
  2: "preparing",
  3: "ready",
};

const READY_STATUS_ID = 14;
const PACKING_STATUS_ID = 21;
const PACKED_STATUS_ID = 22; // change based on your status master

const formatApiTime = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─── Packing ageing → header colour ─────────────────────────────────────────
// Minutes elapsed past an item's target time. Under the warn threshold the
// header keeps its default packing purple; nothing else on the card changes.
const PACKING_WARN_MINUTES = 5;
const PACKING_LATE_MINUTES = 10;

// targetdate is stamped when the item enters Packing; createddate is the
// fallback for rows that predate it.
const itemTargetTime = (item) => {
  const raw = item.targetdate || item.createddate;
  if (!raw) return null;

  const ts = new Date(raw).getTime();
  return Number.isNaN(ts) ? null : ts;
};

// A packing card is only as fresh as its OLDEST item — that item has been
// waiting the longest, so its target time drives the colour for the card.
const earliestTargetTime = (items) =>
  items.reduce((earliest, it) => {
    const ts = itemTargetTime(it);
    if (ts === null) return earliest;
    return earliest === null || ts < earliest ? ts : earliest;
  }, null);

const packingUrgency = (items, now) => {
  const targetTs = earliestTargetTime(items);
  if (targetTs === null) return "";

  const elapsedMinutes = (now - targetTs) / 60000;

  if (elapsedMinutes > PACKING_LATE_MINUTES) return "late";
  if (elapsedMinutes > PACKING_WARN_MINUTES) return "warn";
  return "";
};

// One card PER KITCHEN STATION KOT. Different cuisines (e.g. South Indian
// vs Chinese) are packed by different counters even when they belong to
// the same order — so they must appear as SEPARATE KOT cards.
const kotStationKey = (r) =>
  r.kotnumber || `${r.requestid}-${r.menuitemkitchenid || r.kitchenid || "default"}`;

// One row from GetresActionAllList -> one item. This is the SINGLE source of
// truth this page works off.
const mapActionsToItems = (records, menuItems, modifierItems = []) => {
  if (!Array.isArray(records)) return [];

  return records
    .filter((r) => r.menuId !== null && r.menuId !== undefined)
    .map((r) => {
      const menu = menuItems.find(
        (x) =>
          String(x.value) === String(r.menuId) ||
          String(x.id) === String(r.menuId) ||
          String(x.menuId) === String(r.menuId)
      );

      const isReady =
        r.statusId !== null &&
        r.statusId !== undefined &&
        Number(r.statusId) === READY_STATUS_ID;
      // const isPacking = Number(r.statusId) === PACKING_STATUS_ID;
      const isPacked = Number(r.statusId) === PACKED_STATUS_ID;
      const isPacking = Number(r.statusId) === PACKING_STATUS_ID;

      const hasModifier = r.ModifierID !== null && r.ModifierID !== undefined;
      const modifier = hasModifier
        ? modifierItems.find(
            (x) => String(x.value) === String(r.ModifierID) || String(x.id) === String(r.ModifierID)
          )
        : undefined;

      return {
        ...r,
        id: r.requestactionid,
        requestId: r.requestid,
        // kotKey: kotStationKey(r),
        kotKey: r.requestid,
        kotNo: r.kotnumber,
        tableNo: r.tablenumber,
        tokenNo: r.tokennumber,
        createdTime: formatApiTime(r.createddate),
        actiontype: r.actiontype,
        menuId: r.menuId,
        name: menu?.label || menu?.name || menu?.menuName || menu?.text || `Menu ${r.menuId}`,
        qty: r.menuQty || 1,
        modifierName: hasModifier
          ? modifier?.label || modifier?.name || modifier?.text || `Modifier ${r.ModifierID}`
          : null,
        modifierQty: r.ModifierQty,
        // status: isReady ? "ready" : STATUS_ID_TO_STAGE[r.statusId] || "pending",
        // status: isReady ? "ready" : isPacking ? "pending" : STATUS_ID_TO_STAGE[r.statusId] || "pending",
        // statusId: r.statusId,
        // selected: isReady,
        status: isPacked ? "packed" : isReady ? "ready" : isPacking ? "pending" : STATUS_ID_TO_STAGE[r.statusId] || "pending",

        statusId: r.statusId,

        // Ready items cannot be modified
        selected: false,
      };
    });
};

// Group by kitchen-station KOT. actiontype === 2 -> Packing.
// const groupItemsByKot = (items) => {
//   // const filtered = items.filter((it) => it.actiontype === 2);
//   const filtered = items.filter((it) => Number(it.actiontype) === 2);
//   const grouped = filtered.reduce((acc, it) => {
//     if (!acc[it.kotKey]) {
//       acc[it.kotKey] = {
//         key: it.kotKey,
//         groupType: "kot",
//         kotNo: it.kotNo,
//         tableNo: it.tableNo,
//         tokenNo: it.tokenNo,
//         actiontype: it.actiontype,
//         createdTime: it.createdTime,
//         items: [],
//       };
//     }
//     acc[it.kotKey].items.push(it);
//     return acc;
//   }, {});
//   return Object.values(grouped);
// };

// const groupItemsByOrder = (items) => {
//   const filtered = items.filter(
//     (it) => Number(it.actiontype) === 2
//   );
//   const grouped = items.reduce((acc, it) => {
//     if (!acc[it.requestId]) {
//       acc[it.requestId] = {
//         key: it.requestId,
//         groupType: "order",
//         kotNo: `Order-${String(it.requestId).slice(0, 6)}`,
//         tableNo: it.tableNo,
//         tokenNo: it.tokenNo,
//         actiontype: it.actiontype,
//         createdTime: it.createdTime,
//         relatedKots: new Set(),
//         items: [],
//       };
//     }
//     acc[it.requestId].relatedKots.add(it.kotNo);
//     acc[it.requestId].items.push(it);
//     return acc;
//   }, {});
//   return Object.values(grouped).map((o) => ({
//     ...o,
//     relatedKots: Array.from(o.relatedKots),
//   }));
// };

// const READY_STATUS_ID = 14;

// const PACKING_STATUS_ID = 21;

const groupItemsByOrder = (items) => {

  // const filtered = items.filter(
  //   (it) =>
  //     Number(it.actiontype) === 2 &&
  //     (
  //       Number(it.statusId) === PACKING_STATUS_ID ||
  //       Number(it.statusId) === PACKED_STATUS_ID
  //     )
  // );
  const filtered = items.filter(
    (it) =>
      Number(it.actiontype) === 2 &&
      (
        Number(it.statusId) === PACKING_STATUS_ID ||
        Number(it.statusId) === PACKED_STATUS_ID
      )
  );

  const grouped = filtered.reduce((acc, it) => {

    if (!acc[it.requestId]) {
      acc[it.requestId] = {
        key: it.requestId,
        groupType: "order",
        kotNo: `Order-${String(it.requestId).slice(0, 6)}`,
        tableNo: it.tableNo,
        tokenNo: it.tokenNo,
        actiontype: it.actiontype,
        createdTime: it.createdTime,
        items: [],
      };
    }

    acc[it.requestId].items.push(it);

    return acc;

  }, {});


  return Object.values(grouped);

};

// Groups the summary by menu item. Falls back to the name when menuId is
// missing so two unnamed rows never collapse into one another.
const menuSummaryKey = (item) => String(item.menuId ?? item.name);

// Roll every outstanding (not-yet-packed) item across ALL visible packing
// orders up into one line per menu item. This is what the Packing operator
// reads to know "how many of X still need to be packed" without opening
// every order card. Each row keeps a reference to the ORIGINAL item objects
// it was built from — the expanded child rows and their checkboxes drive
// exactly the same `selected` flag the packing cards do. No parallel state.
const buildMenuSummary = (groups) => {
  const summary = groups.reduce((acc, group) => {
    group.items.forEach((it) => {
      if (Number(it.statusId) === PACKED_STATUS_ID) return;

      const key = menuSummaryKey(it);
      const qty = Number(it.qty) || 0;

      if (!acc[key]) {
        acc[key] = {
          key,
          name: it.name,
          qty: 0,
          pendingQty: 0,
          preparingQty: 0,
          orders: new Set(),
          items: [],
        };
      }

      acc[key].qty += qty;
      if (it.status === "preparing") acc[key].preparingQty += qty;
      else acc[key].pendingQty += qty;
      acc[key].orders.add(group.key);
      acc[key].items.push(it);
    });
    return acc;
  }, {});

  return Object.values(summary)
    .map(({ orders, ...row }) => ({ ...row, orderCount: orders.size }))
    .sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name));
};

class PackingDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      currentTime: this._formatTime(new Date()),
      // Drives the ageing colour on each packing-card header. Kept in state
      // so the re-render is an explicit consequence of the clock, not a
      // side effect.
      now: Date.now(),
      // Summary-panel rows the user has drilled into, keyed by menuSummaryKey.
      expandedMenus: {},
      // Drives the spin animation / disabled state on the Refresh button.
      refreshing: false,
    };
  }

  componentDidMount() {
    // 15s keeps the 5/10 minute header colour changes tight enough to trust.
    this._clockInterval = setInterval(() => {
      this.setState({ currentTime: this._formatTime(new Date()), now: Date.now() });
    }, 15000);

    this.props.loadKotActions();
    this._stopKotLive = this.props.startKotActionsLive();
    this.props.getMasterKeywordList("MenuItem");
    this.props.getMasterKeywordList("Modifier");
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tableLists !== this.props.tableLists && this.state.refreshing) {
      this.setState({ refreshing: false });
    }

    if (
      prevProps.tableLists !== this.props.tableLists ||
      prevProps.MenuItem !== this.props.MenuItem ||
      prevProps.Modifier !== this.props.Modifier
    ) {
      if (!Array.isArray(this.props.tableLists) || this.props.tableLists.length === 0) {
        return;
      }

      const mappedItems = mapActionsToItems(
        this.props.tableLists,
        this.props.MenuItem,
        this.props.Modifier
      );

      this.setState({ items: mappedItems }, () => {
        this.removeReadyKotsAfterReload(mappedItems);
      });
    }
  }

  componentWillUnmount() {
    clearInterval(this._clockInterval);
    if (this._stopKotLive) this._stopKotLive();
  }

  _formatTime(d) {
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  }

  // Tapping the row body cycles Pending ↔ Preparing only.
  handleItemStatusChange = (itemId) => {
    this.setState((prev) => ({
      items: prev.items.map((it) => {
        // if (it.id !== itemId || it.status === "ready") return it;
        if (it.id !== itemId || it.status === "ready" || it.status === "packed")
          return it;
        const nextStatus = toggleKitchenStage(it.status);
        // TODO: persist statusId back to the API, e.g.
        // this.props.updateRequestActionStatus(it.id, nextStatus);
        return { ...it, status: nextStatus };
      }),
    }));
  };

  // handleSelectAll = (groupType, groupKey, checked) => {
  //   this.setState((prev) => ({
  //     items: prev.items.map((it) => {
  //       // const matches = groupType === "kot" ? it.kotKey === groupKey : it.requestId === groupKey;
  //       const matches = it.requestId === groupKey;
  //       if (!matches) return it;
  //       // return { ...it, selected: it.status === "ready" ? false : checked };
  //       return {
  //         ...it,
  //         selected:
  //           it.status === "ready" || it.status === "packed"
  //             ? false
  //             : checked,
  //       };
  //     }),
  //   }));
  // };

  handleSelectAll = (groupType, groupKey, checked) => {
    this.setState((prev) => ({
      items: prev.items.map((it) => {

        const matches = it.requestId === groupKey;

        if (!matches) return it;

        return {
          ...it,
          selected:
            Number(it.statusId) === PACKED_STATUS_ID
              ? false
              : checked,
        };
      }),
    }));
  };

  handleReadyToggle = (itemId, checked) => {
    this.setState((prev) => ({
      items: prev.items.map((it) => (it.id !== itemId ? it : { ...it, selected: checked })),
    }));
  };

  // Re-pulls the whole Packing list from the API; every call is a fresh hit.
  handleRefresh = () => {
    if (this.state.refreshing) return;
    this.setState({ refreshing: true });
    this.props.loadKotActions();
  };

  // handleCollectiveReady = async (groupType, groupKey) => {
  //   const selectedItems = this.state.items.filter((it) => {
  //     // const matches = groupType === "kot" ? it.kotKey === groupKey : it.requestId === groupKey;
  //     const matches = it.requestId === groupKey;
  //     return matches && it.selected;
  //   });

  //   if (selectedItems.length === 0) return;

  //   try {
  //     const data = selectedItems.map((item) => {
  //       const {
  //         id,
  //         requestId,
  //         kotKey,
  //         kotNo,
  //         tableNo,
  //         tokenNo,
  //         createdTime,
  //         name,
  //         qty,
  //         status,
  //         selected,
  //         ...backendData
  //       } = item;

  //       return {
  //         ...backendData,
  //         // update only status
  //         // statusId: READY_STATUS_ID,
  //         statusId: PACKED_STATUS_ID,
  //       };
  //     });

  //     await axios.post("/master/api/UpdateDynamicData?model=Restaurant_Action_Update", {
  //       Key: JSON.stringify(data),
  //     });

  //     this.setState(
  //       (prev) => ({
  //         items: prev.items.map((it) => {
  //           const match = selectedItems.find((x) => x.id === it.id);

  //           if (!match) return it;
  //           // return { ...it, status: "ready", statusId: READY_STATUS_ID, selected: false };
  //           return {
  //             ...it,
  //             status: "packed",
  //             statusId: PACKED_STATUS_ID,
  //             selected: false,
  //           };
  //         }),
  //       }),
  //       () => {
  //         this.removeCompletedKot(groupType, groupKey);
  //       }
  //     );
  //   } catch (err) {
  //     console.log(err);
  //   }
  // };

  // removeCompletedKot = (groupType, groupKey) => {
  //   // const groupItems = this.state.items.filter((item) =>
  //   //   groupType === "kot" ? item.kotKey === groupKey : item.requestId === groupKey
  //   // );

  //   const groupItems = this.state.items.filter(
  //     (item) => item.requestId === groupKey
  //   );

  //   const allReady = groupItems.every((item) => item.status === "ready");
  //   if (!allReady) return;

  //   setTimeout(() => {
  //     this.setState((prev) => ({
  //       items: prev.items.filter((item) =>
  //         // groupType === "kot" ? item.kotKey !== groupKey : item.requestId !== groupKey
  //         item.requestId !== groupKey
  //       ),
  //     }));
  //   });
  // };

  // removeCompletedKot = (groupType, groupKey) => {

  //   const groupItems = this.state.items.filter(
  //     (item) => item.requestId === groupKey
  //   );


  //   const allPacked = groupItems.every(
  //     (item) => Number(item.statusId) === PACKED_STATUS_ID
  //   );


  //   if (!allPacked) return;


  //   setTimeout(() => {

  //     this.setState((prev) => ({
  //       items: prev.items.filter(
  //         (item) => item.requestId !== groupKey
  //       )
  //     }));

  //   });

  // };


  toggleSummaryExpand = (menuKey) => {
    this.setState((prev) => ({
      expandedMenus: {
        ...prev.expandedMenus,
        [menuKey]: !prev.expandedMenus[menuKey],
      },
    }));
  };

  // Menu-item-level select in the summary panel: ticks every outstanding
  // (not-yet-packed) line of that menu item across every packing order.
  // Writes the same `selected` flag the packing cards read, so both
  // presentations stay in lock-step.
  handleSummarySelectMenu = (menuKey, checked) => {
    this.setState((prev) => ({
      items: prev.items.map((it) => {
        if (Number(it.actiontype) !== 2 || Number(it.statusId) === PACKED_STATUS_ID) return it;
        if (menuSummaryKey(it) !== menuKey) return it;
        return { ...it, selected: checked };
      }),
    }));
  };

  // The single, page-level Mark Packed action. Replaces the per-card
  // buttons — whatever is ticked anywhere on the board (card or summary
  // panel) goes packed in one call.
  handleGlobalMarkPacked = () => {
    const selectedItems = this.state.items.filter(
      (it) =>
        Number(it.actiontype) === 2 &&
        it.selected &&
        Number(it.statusId) !== PACKED_STATUS_ID
    );

    this.markItemsPacked(selectedItems);
  };

  // Marks an arbitrary set of items packed. The set may span several
  // orders, so every order it touches is re-checked for completion after.
  markItemsPacked = async (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) return;

    const affectedOrders = new Set(selectedItems.map((it) => it.requestId));
    const selectedIds = new Set(selectedItems.map((it) => it.id));

    try {
      const data = selectedItems.map((item) => {
        const {
          id,
          requestId,
          kotKey,
          kotNo,
          tableNo,
          tokenNo,
          createdTime,
          name,
          qty,
          status,
          selected,
          ...backendData
        } = item;

        return {
          ...backendData,
          statusId: PACKED_STATUS_ID,
        };
      });

      await axios.post(
        "/master/api/UpdateDynamicData?model=Restaurant_Action_Update",
        {
          Key: JSON.stringify(data),
        }
      );

      this.setState(
        (prev) => ({
          items: prev.items.map((it) => {
            if (!selectedIds.has(it.id)) return it;

            return {
              ...it,
              status: "packed",
              statusId: PACKED_STATUS_ID,
              selected: false,
            };
          }),
        }),
        () => {
          affectedOrders.forEach((orderId) => {
            this.removeCompletedKot("order", orderId);
          });
        }
      );
    } catch (err) {
      console.log("Mark Packed Error:", err);
    }
  };
  // removeCompletedKot = (groupType, groupKey) => {

  //   const groupItems = this.state.items.filter(
  //     (item) => item.requestId === groupKey
  //   );

  //   const allPacked = groupItems.length > 0 &&
  //     groupItems.every(
  //       (item) => Number(item.statusId) === PACKED_STATUS_ID
  //     );

  //   if (!allPacked) return;

  //   this.setState((prev) => ({
  //     items: prev.items.filter(
  //       (item) => item.requestId !== groupKey
  //     )
  //   }));
  // };
  // removeCompletedKot = (groupType, groupKey) => {

  //   this.setState((prev) => {

  //     const groupItems = prev.items.filter(
  //       (item) => item.requestId === groupKey
  //     );

  //     const allPacked =
  //       groupItems.length > 0 &&
  //       groupItems.every(
  //         (item) => Number(item.statusId) === PACKED_STATUS_ID
  //       );

  //     if (!allPacked) {
  //       return null;
  //     }

  //     return {
  //       items: prev.items.filter(
  //         (item) => item.requestId !== groupKey
  //       )
  //     };

  //   });
  // };

  removeCompletedKot = (groupType, groupKey) => {
    this.setState((prev) => {
      const groupItems = prev.items.filter(
        (item) => item.requestId === groupKey
      );

      const allPacked =
        groupItems.length > 0 &&
        groupItems.every(
          (item) => Number(item.statusId) === PACKED_STATUS_ID
        );

      if (!allPacked) {
        return null;
      }

      return {
        items: prev.items.filter(
          (item) => item.requestId !== groupKey
        ),
      };
    });
  };
  removeReadyKotsAfterReload = (items) => {
    const readyGroups = {};

    items.forEach((item) => {
      if (Number(item.statusId) === READY_STATUS_ID) {
        if (!readyGroups[item.kotKey]) readyGroups[item.kotKey] = [];
        readyGroups[item.kotKey].push(item);
      }
    });

    Object.keys(readyGroups).forEach((key) => {
      const groupItems = items.filter((item) => item.kotKey === key);
      const allReady = groupItems.every((item) => Number(item.statusId) === READY_STATUS_ID);

      if (allReady) {
        setTimeout(() => {
          this.setState((prev) => ({
            items: prev.items.filter((item) => item.kotKey !== key),
          }));
        });
      }
    });
  };

  // One menu-item line + its (collapsed by default) per-order breakdown.
  renderSummaryGroup = (row) => {
    const expanded = !!this.state.expandedMenus[row.key];

    // row.items only ever holds outstanding (not-yet-packed) lines, so
    // "all" means all of them.
    const selectedCount = row.items.filter((it) => it.selected).length;
    const allSelected = selectedCount === row.items.length;
    const someSelected = selectedCount > 0 && !allSelected;

    return (
      <div key={row.key} className="kot-summary-group">
        <div
          className={`kot-summary-row ${someSelected ? "kot-summary-row--partial" : ""}`}
        >
          <Checkbox
            checked={allSelected}
            onChange={(e) => this.handleSummarySelectMenu(row.key, e.checked)}
            className="kot-ready-check kot-summary-check"
          />

          <span className="kot-summary-qty">{row.qty}</span>

          <div
            className="kot-summary-info"
            onClick={() => this.toggleSummaryExpand(row.key)}
          >
            <span className="kot-summary-name">{row.name}</span>
            <span className="kot-summary-meta">
              {row.orderCount} order{row.orderCount === 1 ? "" : "s"}
              {row.preparingQty > 0 && (
                <>
                  <span className="dot">&middot;</span>
                  {row.preparingQty} preparing
                </>
              )}
              {selectedCount > 0 && (
                <>
                  <span className="dot">&middot;</span>
                  <span className="kot-summary-meta--selected">
                    {selectedCount} selected
                  </span>
                </>
              )}
            </span>
          </div>

          <button
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse breakdown" : "Expand breakdown"}
            className="kot-summary-expand"
            onClick={() => this.toggleSummaryExpand(row.key)}
          >
            <i className={`pi ${expanded ? "pi-chevron-up" : "pi-chevron-down"}`} />
          </button>
        </div>

        {expanded && (
          <div className="kot-summary-children">
            {row.items.map((it) => (
              <div key={it.id} className="kot-summary-child">
                <Checkbox
                  checked={it.selected || false}
                  onChange={(e) => this.handleReadyToggle(it.id, e.checked)}
                  className="kot-ready-check kot-summary-check"
                />

                <span className="kot-summary-child-qty">&times;{it.qty}</span>

                <div className="kot-summary-child-info">
                  <span className="kot-summary-child-kot">
                    {it.kotNo || `Order-${String(it.requestId).slice(0, 6)}`}
                  </span>
                  <span className="kot-summary-child-meta">
                    {it.tableNo ? `Table ${it.tableNo}` : `Token ${it.tokenNo}`}
                    <span className="dot">&middot;</span>
                    <span
                      className={`kot-summary-child-stage kot-summary-child-stage--${it.status}`}
                    >
                      {it.status === "preparing" ? "Preparing" : "Pending"}
                    </span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  renderMenuSummary = (summaryRows, totalQty, orderCount) => (
    <aside className="kot-summary-panel">
      <div className="kot-summary-panel__header">
        <div className="kot-summary-panel__title-row">
          <i className="pi pi-list kot-summary-panel__icon" />
          <h3 className="kot-summary-panel__title">Item Summary</h3>
        </div>
        <span className="kot-summary-panel__subtitle">
          {totalQty} item{totalQty === 1 ? "" : "s"} &middot; {orderCount} order
          {orderCount === 1 ? "" : "s"}
        </span>
      </div>

      {summaryRows.length === 0 ? (
        <div className="kot-summary-empty">
          <i className="pi pi-check-circle kot-summary-empty__icon" />
          <span>Queue is clear</span>
        </div>
      ) : (
        <div className="kot-summary-list">
          {summaryRows.map(this.renderSummaryGroup)}
        </div>
      )}
    </aside>
  );

  renderKOTCard = (group) => {
    const allPacked = group.items.every(
      item => Number(item.statusId) === PACKED_STATUS_ID
    );

    if (allPacked) {
      return null;
    }

    const selectedCount = group.items.filter(
      (i) => i.selected && Number(i.statusId) !== PACKED_STATUS_ID
    ).length;
    const urgency = packingUrgency(group.items, this.state.now);

    return (
      <div key={group.key} className="kot-card">
        <div
          className={`kot-card__header kot-card__header--packing ${urgency ? `kot-card__header--${urgency}` : ""
            }`}
        >
          <div className="kot-card__header-top">
            <span className="kot-card__kot-no">{group.kotNo}</span>
            <small className="kot-card__time">
              <i className="pi pi-clock p-mr-1" />
              {group.createdTime}
            </small>
          </div>

          <div className="kot-card__meta">
            {group.tableNo && (
              <>
                Table {group.tableNo} <span className="dot">&middot;</span>
              </>
            )}
            Token {group.tokenNo}
            <span className="dot">&middot;</span>
            Packing
          </div>
        </div>

        <div className="kot-card__items">
          <div className="kot-select-all">
            <span className="kot-select-all-label">Select All</span>

            {/* <Checkbox
              checked={
                group.items.filter(
                  (item) => Number(item.statusId) !== PACKED_STATUS_ID
                ).length > 0 &&
                group.items
                  .filter((item) => item.status !== "ready")
                  .every((item) => item.selected)
              }
              onChange={(e) => this.handleSelectAll(group.groupType, group.key, e.checked)}
              className="kot-select-all-checkbox"
            /> */}
            <Checkbox
              checked={
                group.items.filter(
                  (item) => Number(item.statusId) !== PACKED_STATUS_ID
                ).length > 0 &&
                group.items
                  .filter(
                    (item) => Number(item.statusId) !== PACKED_STATUS_ID
                  )
                  .every((item) => item.selected)
              }
              onChange={(e) =>
                this.handleSelectAll(group.groupType, group.key, e.checked)
              }
              className="kot-select-all-checkbox"
            />
          </div>

          {group.items.map((item) => {

            const isPacked = Number(item.statusId) === PACKED_STATUS_ID;
            const isSelected = item.selected || false;

            return (
              <div
                key={item.id}
                className={`kot-item-row ${isPacked ? "kot-item-row--done" : ""}`}
                onClick={() => !isPacked && this.handleItemStatusChange(item.id)}
              >
                <span className="kot-item-qty">&times;{item.qty}</span>

                <span className="kot-item-info">
                  <span className={`kot-item-name ${isPacked ? "kot-item-name--done" : ""}`}>
                    {item.name}
                  </span>
                  {item.modifierName && (
                    <span className="kot-item-modifier">
                      {item.modifierName}
                      {item.modifierQty ? ` ×${item.modifierQty}` : ""}
                    </span>
                  )}
                </span>

                <span
                  className={`kot-ready-pill ${isPacked ? "kot-ready-pill--active" : ""}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className={`kot-ready-label ${isPacked ? "kot-ready-label--active" : ""}`}>
                    Packed
                  </span>

                  <Checkbox
                    checked={isPacked || isSelected}
                    disabled={isPacked}
                    onChange={(e) => this.handleReadyToggle(item.id, e.checked)}
                    className="kot-ready-check"
                  />
                </span>
              </div>
            );
          })}
        </div>

        <div className="kot-card__footer">
          {allPacked ? (
            <span className="kot-all-packed-badge">
              <i className="pi pi-check-circle" />
              All Packed
            </span>
          ) : (
            <span
              className={`kot-card__selection ${selectedCount > 0 ? "kot-card__selection--active" : ""
                }`}
            >
              {selectedCount > 0
                ? `${selectedCount} of ${group.items.length} selected`
                : "Tick items, then Mark Packed"}
            </span>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { items, currentTime } = this.state;
    const packingGroups = groupItemsByOrder(items).filter(
      (group) =>
        !group.items.every(
          (item) => Number(item.statusId) === PACKED_STATUS_ID
        )
    );

    const summaryRows = buildMenuSummary(packingGroups);
    const summaryTotalQty = summaryRows.reduce((sum, row) => sum + row.qty, 0);

    const selectableItems = packingGroups.reduce(
      (acc, group) =>
        acc.concat(
          group.items.filter((it) => Number(it.statusId) !== PACKED_STATUS_ID)
        ),
      []
    );
    const selectedCount = selectableItems.filter((it) => it.selected).length;

    return (
      <div className="kot-dashboard-root kot-dashboard-root--split">
        {this.props.loading && <div className="page-loader"></div>}
        {this.props.error !== null && this.props.error !== undefined && (
          <div className="p-error" style={{ padding: "0.5em 1em" }}>
            {this.props.error}
          </div>
        )}

        <div className="kot-page-header">
          <div className="kot-page-header__brand">
            <div className="kot-page-header__title-row">
              <i className="pi pi-box kot-page-header__icon" />
              <h2 className="kot-page-header__title">Packing</h2>
              <span className="kot-tab-count kot-tab-count--active">{packingGroups.length}</span>
            </div>
          </div>

          <div className="kot-page-header__actions">
            <small className="kot-page-header__time">
              <i className="pi pi-clock p-mr-1" />
              {currentTime}
            </small>

            <Button
              icon={`pi pi-refresh ${this.state.refreshing ? "kot-refresh-btn__icon--spin" : ""}`}
              label={this.state.refreshing ? "Refreshing" : "Refresh"}
              className="p-button-sm kot-refresh-btn"
              onClick={this.handleRefresh}
              disabled={this.state.refreshing}
              aria-label="Refresh"
              tooltip="Refresh Packing data"
              tooltipOptions={{ position: "bottom" }}
            />

            <Button
              label={selectedCount > 0 ? `Mark Packed (${selectedCount})` : "Mark Packed"}
              icon="pi pi-check"
              disabled={selectedCount === 0}
              className="p-button-sm kot-mark-ready-btn kot-mark-ready-btn--global"
              onClick={this.handleGlobalMarkPacked}
            />
          </div>
        </div>

        <div className="kot-dashboard-body">
          <div className="kot-dashboard-main">
            {packingGroups.length === 0 ? (
              <div className="kot-empty-state">
                <i className="pi pi-inbox kot-empty-state__icon" />
                <span className="p-text-secondary">No active Packing KOTs</span>
              </div>
            ) : (
              <div className="kot-card-grid">{packingGroups.map(this.renderKOTCard)}</div>
            )}
          </div>

          {this.renderMenuSummary(summaryRows, summaryTotalQty, packingGroups.length)}
        </div>
      </div>
    );
  }
}

// ─── Redux wiring ─────────────────────────────────────────────────────────────
const mapStateToProps = (state) => ({
  loading: state.master.loading,
  error: state.master.error,
  success: state.master.success,
  // The action list has its own store slot, kept live by startKotActionsLive.
  tableLists: state.master.kotActions,
  menuItems: state.keyword.keywordList,
  MenuItem: state.keyword.MenuItem !== undefined ? state.keyword.MenuItem : [],
  Modifier: state.keyword.Modifier !== undefined ? state.keyword.Modifier : [],
});

const mapDispatchToProps = (dispatch) => ({
  getMasterTableList: (table, company) => dispatch(actions.getMasterTableList(table, company)),
  loadKotActions: () => dispatch(actions.loadKotActions()),
  startKotActionsLive: () => dispatch(actions.startKotActionsLive()),
  getMasterKeywordList: (keyword) => dispatch(actions.getMasterKeywordList(keyword)),
  // TODO: once you have a real "update status" endpoint, wire it here.
});

export default connect(mapStateToProps, mapDispatchToProps)(PackingDashboard);